/**
 * Locks & Keys-laag op de bodygraph buiten de modus (spec
 * `2026-10-10-hd-locks-keys-basics-design.md` §3).
 *
 * Twee delen, beide pure SVG-markup in viewBox-coördinaten van de
 * bodygraph-SVG (zelfde aanpak als de monopool-laag):
 * - ringen rond de lock-poorten (G-geel met gouden rand, de lock-taal van de
 *   modus) en rond de keys van de periode (dusk-blauw). De chart-component
 *   zet ze in een laag bóven alle poortcirkels: een ring mag nooit onder de
 *   achtergrondcirkel van een buurpoort verdwijnen (Steven 2026-10-10, bv. 42
 *   en 9 naast 3);
 * - een iconenlaag bovenop: een slot-badge in het midden van het G-center en
 *   een sleutel-badge naast elk center met key-poorten, op een vaste plek in de
 *   witruimte ({@link HD_LOCKS_KEYS_KEY_BADGE_ANCHORS}).
 *
 * Welke poorten en badges getoond worden, bepaalt
 * {@link planHdBodygraphLocksKeysOverlay}: alle locks + keys van de periode, of
 * enkel de poorten die de chart van die bodygraph activeert.
 */

import { HD_BODYGRAPH_GATE_TO_CENTER } from './hd-bodygraph-metadata';
import { HdBodygraphCenter } from './hd-bodygraph.models';
import { HD_LOCKS_KEYS_LOCKS, HdLocksKeysPeriodId, hdLocksKeysKeyGate } from '../mandala/locks-keys/hd-locks-keys-metadata';

/** Input van de chart-component; `null` = laag uit. */
export interface HdBodygraphLocksKeysOverlay {
  readonly periodId: HdLocksKeysPeriodId;
  /** `all` = alle locks + keys van de periode; `chart` = enkel geactiveerde poorten. */
  readonly mode: 'all' | 'chart';
}

/** Wat er effectief getekend wordt voor één bodygraph. */
export interface HdBodygraphLocksKeysOverlayPlan {
  readonly lockGates: readonly number[];
  readonly keyGates: readonly number[];
  /** Slot-badge in het G-center: in `chart` enkel bij minstens één geactiveerde lock. */
  readonly showLockBadge: boolean;
  /** Centers met minstens één (getoonde) key-poort: krijgen een sleutel-badge. */
  readonly keyCenters: readonly HdBodygraphCenter[];
}

export function planHdBodygraphLocksKeysOverlay(
  overlay: HdBodygraphLocksKeysOverlay,
  activatedGates: ReadonlySet<number>,
): HdBodygraphLocksKeysOverlayPlan {
  const shown = (gate: number): boolean => overlay.mode === 'all' || activatedGates.has(gate);
  const lockGates = HD_LOCKS_KEYS_LOCKS.map(lock => lock.gate).filter(shown);
  const keyGates = HD_LOCKS_KEYS_LOCKS.map(lock => hdLocksKeysKeyGate(lock.gate, overlay.periodId)).filter(shown);
  const keyCenters: HdBodygraphCenter[] = [];
  for (const gate of keyGates) {
    const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];
    if (center != null && !keyCenters.includes(center)) {
      keyCenters.push(center);
    }
  }
  return { lockGates, keyGates, showLockBadge: lockGates.length > 0, keyCenters };
}

export interface HdLocksKeysLayerRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Een poortcirkel in viewBox-coördinaten. De poortcirkels in het asset zijn
 * licht ovaal (rx ~21,9 / ry ~21,4), dus de ringen volgen rx/ry apart.
 */
export interface HdLocksKeysLayerEllipse {
  readonly cx: number;
  readonly cy: number;
  readonly rx: number;
  readonly ry: number;
}

/**
 * Plek van de sleutel-badge per center, als fractie van de center-bbox
 * (0,0 = linksboven, 1,1 = rechtsonder; buiten [0,1] = naast het center).
 * Gekozen op de echte `bodygraph-full.svg` (viewBox 1182×1772): telkens in
 * witruimte, vrij van poortcirkels, kanalen en andere centers, aan de kant
 * van de key-poorten. De spec toetst dit tegen de gemeten geometrie. Ajna en
 * G dragen nooit een key.
 */
export const HD_LOCKS_KEYS_KEY_BADGE_ANCHORS: Readonly<Partial<Record<HdBodygraphCenter, { fx: number; fy: number }>>> = {
  // Rechts naast de poortrij 64-61-63, in de lege ruimte naast de hoofddriehoek.
  hoofd: { fx: 1.132, fy: 0.833 },
  // Linksboven naast 62/16/20, boven het vertrekpunt van de diagonale kanalen.
  keel: { fx: -0.209, fy: 0.174 },
  // Onder het hart, tussen het 26-44-kanaal en het 40-37-kanaal.
  hart: { fx: 0.46, fy: 1.169 },
  // Linksboven de milt-punt (bij 48/57), links van de opgaande kanalen.
  milt: { fx: -0.12, fy: -0.167 },
  // Spiegelbeeld van de milt: rechtsboven de solar-plexus-punt (bij 36).
  solarplexus: { fx: 1.082, fy: -0.167 },
  // Linksonder het sacraal (bij 42/34), boven de miltkanalen naar de wortel.
  sacraal: { fx: -0.174, fy: 1.09 },
  // Linksonder de wortel (bij 54/58), onder de miltkanalen.
  root: { fx: -0.189, fy: 1.005 },
};

/** Straal van de sleutel-badge (viewBox-eenheden; een poortcirkel is ~22). */
export const HD_LOCKS_KEYS_KEY_BADGE_RADIUS = 22;
/** Straal van de slot-badge in het G-center. */
export const HD_LOCKS_KEYS_LOCK_BADGE_RADIUS = 27;

export function hdLocksKeysKeyBadgeCenter(
  center: HdBodygraphCenter,
  bbox: HdLocksKeysLayerRect,
): { x: number; y: number } | null {
  const anchor = HD_LOCKS_KEYS_KEY_BADGE_ANCHORS[center];
  if (!anchor) {
    return null;
  }
  return { x: bbox.x + bbox.width * anchor.fx, y: bbox.y + bbox.height * anchor.fy };
}

// Kleuren: dezelfde tokens als de modus (styles/_variables.scss), met de
// literal als fallback omdat de laag als losse SVG-markup geïnjecteerd wordt.
const KEY_COLOR = 'var(--hd-locks-keys-color, #2c4a6e)';
const LOCK_OUTLINE = 'var(--hd-locks-keys-lock-outline, #b8962e)';
const LOCK_YELLOW = '#fdf467';
const LOCK_GLYPH_COLOR = '#6b5312';

// Font Awesome 7 Pro solid (zelfde webfont als de L&K-iconen op de mandala).
const LOCK_GLYPH = '';
const KEY_GLYPH = '';

function fmt(value: number): string {
  return value.toFixed(2);
}

/**
 * Ring rond één poortcirkel, bovenop getekend. De band begint een halve
 * eenheid binnen de cirkelrand (geen spleetje met de achtergrond) en loopt
 * ~7,5 eenheden naar buiten. Lock: goud-geel-goud (leesbaar op het gele én het
 * witte G-center). Key: een smalle witte spleet en daarbuiten dusk-blauw — de
 * spleet is nodig omdat dusk-blauw direct tegen de zwarte poortcirkel op
 * chartgrootte wegvalt (geverifieerd op ~0,5× schaal).
 */
export function buildHdLocksKeysGateRingMarkup(
  kind: 'lock' | 'key',
  gate: number,
  ellipse: HdLocksKeysLayerEllipse,
): string {
  const ring = (offset: number, width: number, paint: string): string =>
    `<ellipse cx="${fmt(ellipse.cx)}" cy="${fmt(ellipse.cy)}" rx="${fmt(ellipse.rx + offset)}" ry="${fmt(ellipse.ry + offset)}"`
    + ` fill="none" ${paint} stroke-width="${width}"/>`;
  if (kind === 'lock') {
    return `<g class="hd-locks-keys-ring hd-locks-keys-ring--lock" data-gate="${gate}">`
      + ring(3.5, 8, `style="stroke:${LOCK_OUTLINE}"`)
      + ring(3.5, 4.4, `stroke="${LOCK_YELLOW}"`)
      + `</g>`;
  }
  return `<g class="hd-locks-keys-ring hd-locks-keys-ring--key" data-gate="${gate}">`
    + ring(1, 2.8, 'stroke="#ffffff"')
    + ring(5, 5.2, `style="stroke:${KEY_COLOR}"`)
    + `</g>`;
}

export const HD_LOCKS_KEYS_BADGE_LAYER_CLASS = 'hd-locks-keys-badges';

/**
 * Bovenste laag met de badges: slot in het midden van het G-center (gele schijf
 * met gouden rand, zoals de lock-chips) en per key-center een dusk-blauwe
 * schijf met wit sleuteltje (zoals een actieve key-chip). Pointer-inert.
 */
export function buildHdLocksKeysBadgeLayerMarkup(
  gCenter: HdLocksKeysLayerRect | null,
  keyBadges: readonly { center: HdBodygraphCenter; x: number; y: number }[],
): string {
  let markup = `<g class="${HD_LOCKS_KEYS_BADGE_LAYER_CLASS}" pointer-events="none" aria-hidden="true">`;
  if (gCenter) {
    const x = fmt(gCenter.x + gCenter.width / 2);
    const y = fmt(gCenter.y + gCenter.height / 2);
    const r = HD_LOCKS_KEYS_LOCK_BADGE_RADIUS;
    markup += `<g class="hd-locks-keys-badge hd-locks-keys-badge--lock" data-center="zelf">`
      + `<circle cx="${x}" cy="${y}" r="${r}" fill="${LOCK_YELLOW}" style="stroke:${LOCK_OUTLINE}" stroke-width="2.5"/>`
      + `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="central" fill="${LOCK_GLYPH_COLOR}"`
      + ` style="font-family:'Font Awesome 7 Pro';font-weight:900;font-size:${fmt(r * 0.95)}px">${LOCK_GLYPH}</text>`
      + `</g>`;
  }
  for (const badge of keyBadges) {
    const x = fmt(badge.x);
    const y = fmt(badge.y);
    const r = HD_LOCKS_KEYS_KEY_BADGE_RADIUS;
    markup += `<g class="hd-locks-keys-badge hd-locks-keys-badge--key" data-center="${badge.center}">`
      + `<circle cx="${x}" cy="${y}" r="${r}" style="fill:${KEY_COLOR}"/>`
      + `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="central" fill="#ffffff"`
      + ` style="font-family:'Font Awesome 7 Pro';font-weight:900;font-size:${fmt(r * 0.95)}px">${KEY_GLYPH}</text>`
      + `</g>`;
  }
  return `${markup}</g>`;
}
